import * as React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import useAuthorize from '../hooks/useAuthorize';
import { DASHBOARD_MODULE_ROLES } from '../roles/portalRoles';
import DashboardPanelHeader from '../components/DashboardPanelHeader';
import { AnalyticsFreshnessNote } from '../utils/AnalyticsFreshnessNote';
import {
  fetchHeroProductAffinity,
  heroBestPriceUrl,
  HeroAffinityResponse,
} from './heroAffinityApi';
import {
  CustomerBreadthHistogramChart,
  CustomerLandscapeBarChart,
  CustomerProductMatrixChart,
  CustomerScoreBucketHeatmap,
  HeroLeaderboardChart,
  ProductLandscapeBarChart,
  ProductScoreBucketHeatmap,
  ProductTierDonutChart,
} from './HeroAffinityCharts';

const countFmt = (n: number) => Number(n || 0).toLocaleString('en-IN');

const DashboardProductAffinity = () => {
  useAuthorize(DASHBOARD_MODULE_ROLES);

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [data, setData] = React.useState<HeroAffinityResponse | null>(null);
  const [cellMetric, setCellMetric] = React.useState<'score' | 'score_pct_of_row'>('score');
  const [customerRankBy, setCustomerRankBy] = React.useState<'total_score' | 'max_score'>('total_score');
  const [matrixSize, setMatrixSize] = React.useState(15);
  const [customerIdSearch, setCustomerIdSearch] = React.useState('');
  const [selectedPair, setSelectedPair] = React.useState<{ customerId: number; productId: number } | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = {
        view: 'matrix',
        matrix_size: matrixSize,
        customer_rank_by: customerRankBy,
        cell_metric: cellMetric,
      };
      if (customerIdSearch.trim()) {
        params.view = 'drilldown';
        params.customer_id = customerIdSearch.trim();
      }
      const payload = await fetchHeroProductAffinity(params);
      setData(payload);
    } catch (e: unknown) {
      const axiosMsg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      const message = axiosMsg
        || (e instanceof Error ? e.message : 'Failed to load affinity data');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [cellMetric, customerRankBy, matrixSize, customerIdSearch]);

  React.useEffect(() => {
    load();
  }, [load]);

  const freshness = data?.source_freshness?.scores_computed_at_max
    || data?.summary?.scores_computed_at_max
    || null;

  const handleCellClick = (customerId: number, productId: number) => {
    setSelectedPair({ customerId, productId });
  };

  return (
    <Box sx={{ p: 2 }}>
      <DashboardPanelHeader
        title="Product affinity (hero scoring)"
        subheader="Top customers × top products from nightly relevance rollup (CRE-64)"
        exportFilename="product-affinity.xlsx"
      />

      {freshness && (
        <AnalyticsFreshnessNote
          dataThrough={freshness.slice(0, 10)}
          lastUpdatedAt={freshness}
        />
      )}

      <Grid container spacing={2} sx={{ mt: 2, mb: 2 }}>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Cell metric</InputLabel>
            <Select
              value={cellMetric}
              label="Cell metric"
              onChange={(e) => setCellMetric(e.target.value as 'score' | 'score_pct_of_row')}
            >
              <MenuItem value="score">Affinity score</MenuItem>
              <MenuItem value="score_pct_of_row">% of row max</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Rank customers by</InputLabel>
            <Select
              value={customerRankBy}
              label="Rank customers by"
              onChange={(e) => setCustomerRankBy(e.target.value as 'total_score' | 'max_score')}
            >
              <MenuItem value="total_score">Total affinity</MenuItem>
              <MenuItem value="max_score">Best single product</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Grid size</InputLabel>
            <Select
              value={matrixSize}
              label="Grid size"
              onChange={(e) => setMatrixSize(Number(e.target.value))}
            >
              {[10, 15, 20].map((n) => (
                <MenuItem key={n} value={n}>{n} × {n}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="Customer ID drill-down"
            value={customerIdSearch}
            onChange={(e) => setCustomerIdSearch(e.target.value)}
            placeholder="Optional — loads top products for customer"
          />
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          {error.includes('relation') && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Ensure CRE65 migration and hero relevance rollup have run on this environment.
            </Typography>
          )}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && data && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">Scored pairs</Typography>
                <Typography variant="h6">{countFmt(data.summary.scored_pairs)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">Hero assigned</Typography>
                <Typography variant="h6">{countFmt(data.summary.customers_with_hero)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">Products in scores</Typography>
                <Typography variant="h6">{countFmt(data.summary.distinct_products)}</Typography>
              </Paper>
            </Grid>
            {selectedPair && (
              <Grid item xs={12} sm={3}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">Selected cell</Typography>
                  <Link
                    href={heroBestPriceUrl(selectedPair.customerId, selectedPair.productId)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open best-price quote
                  </Link>
                </Paper>
              </Grid>
            )}
          </Grid>

          {data.matrix && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top {data.matrix.matrix_size} customers × top {data.matrix.matrix_size} products
                </Typography>
                <CustomerProductMatrixChart
                  matrix={data.matrix}
                  onCellClick={handleCellClick}
                />
              </CardContent>
            </Card>
          )}

          {data.hero_leaderboard && data.hero_leaderboard.length > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Hero product assignment</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Customers whose single assigned hero product is each SKU (narrower than affinity breadth below).
                </Typography>
                <HeroLeaderboardChart items={data.hero_leaderboard} />
              </CardContent>
            </Card>
          )}

          {data.product_landscape && data.product_landscape.products.length > 0 && (
            <>
              <Typography variant="h6" sx={{ mb: 1, mt: 1 }}>
                Product heat landscape
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {data.product_landscape.tier_rules.description}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, borderLeft: 4, borderColor: 'error.main' }}>
                    <Typography variant="caption" color="text.secondary">Hot products</Typography>
                    <Typography variant="h6">{countFmt(data.product_landscape.tier_summary.hot)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, borderLeft: 4, borderColor: 'warning.main' }}>
                    <Typography variant="caption" color="text.secondary">Medium</Typography>
                    <Typography variant="h6">{countFmt(data.product_landscape.tier_summary.medium)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, borderLeft: 4, borderColor: 'grey.500' }}>
                    <Typography variant="caption" color="text.secondary">Low</Typography>
                    <Typography variant="h6">{countFmt(data.product_landscape.tier_summary.low)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">Scored products (table)</Typography>
                    <Typography variant="h6">{countFmt(data.product_landscape.products.length)}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>Hot / medium / low mix</Typography>
                      <ProductTierDonutChart summary={data.product_landscape.tier_summary} />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Top products by buyer affinity
                      </Typography>
                      <ProductLandscapeBarChart landscape={data.product_landscape} />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Score strength heatmap (top {data.product_landscape.chart_products.length} products)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    How many buyers fall in each affinity score band per product.
                  </Typography>
                  <ProductScoreBucketHeatmap landscape={data.product_landscape} />
                </CardContent>
              </Card>

              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>All scored products</Typography>
                  <TableContainer sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell>Tier</TableCell>
                          <TableCell align="right">Buyers</TableCell>
                          <TableCell align="right">Avg score</TableCell>
                          <TableCell align="right">Sum score</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.product_landscape.products.map((row) => (
                          <TableRow key={row.product_id}>
                            <TableCell>{row.product_name}</TableCell>
                            <TableCell sx={{ textTransform: 'capitalize' }}>{row.tier}</TableCell>
                            <TableCell align="right">{countFmt(row.buyer_count)}</TableCell>
                            <TableCell align="right">{row.avg_score.toFixed(4)}</TableCell>
                            <TableCell align="right">{row.sum_score.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </>
          )}

          {data.customer_landscape && data.customer_landscape.breadth_histogram.length > 0 && (
            <>
              <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>
                Customer heat landscape
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {data.customer_landscape.tier_rules.description}
                {data.customer_landscape.max_product_count > 0 && (
                  <> Max breadth: {data.customer_landscape.max_product_count} products in one list.</>
                )}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, borderLeft: 4, borderColor: 'error.main' }}>
                    <Typography variant="caption" color="text.secondary">Hot customers</Typography>
                    <Typography variant="h6">{countFmt(data.customer_landscape.tier_summary.hot)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, borderLeft: 4, borderColor: 'warning.main' }}>
                    <Typography variant="caption" color="text.secondary">Medium</Typography>
                    <Typography variant="h6">{countFmt(data.customer_landscape.tier_summary.medium)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, borderLeft: 4, borderColor: 'grey.500' }}>
                    <Typography variant="caption" color="text.secondary">Low</Typography>
                    <Typography variant="h6">{countFmt(data.customer_landscape.tier_summary.low)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">In breadth table</Typography>
                    <Typography variant="h6">{countFmt(data.customer_landscape.customers.length)}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Customers by number of affinity products
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Bar height = how many customers have exactly this many scored products (deepest lists on the left).
                  </Typography>
                  <CustomerBreadthHistogramChart landscape={data.customer_landscape} />
                </CardContent>
              </Card>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>Customer tier mix</Typography>
                      <ProductTierDonutChart summary={data.customer_landscape.tier_summary} />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Widest affinity lists (top customers)
                      </Typography>
                      <CustomerLandscapeBarChart landscape={data.customer_landscape} />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Score mix heatmap (top {data.customer_landscape.chart_customers.length} customers)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    For each customer, how many affinity products fall in each score band.
                  </Typography>
                  <CustomerScoreBucketHeatmap landscape={data.customer_landscape} />
                </CardContent>
              </Card>

              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>Customers by affinity breadth</Typography>
                  <TableContainer sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Customer</TableCell>
                          <TableCell>Tier</TableCell>
                          <TableCell align="right">Products</TableCell>
                          <TableCell align="right">Avg score</TableCell>
                          <TableCell align="right">Sum score</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.customer_landscape.customers.map((row) => (
                          <TableRow key={row.customer_id}>
                            <TableCell>{row.customer_name}</TableCell>
                            <TableCell sx={{ textTransform: 'capitalize' }}>{row.tier}</TableCell>
                            <TableCell align="right">{countFmt(row.product_count)}</TableCell>
                            <TableCell align="right">{row.avg_score.toFixed(4)}</TableCell>
                            <TableCell align="right">{row.sum_score.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </>
          )}

          {!loading && data && data.summary.scored_pairs === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No affinity scores in the database yet. On UAT, run the hero relevance rollup once
              (ECS task <code>runHeroProductRelevanceRollup.js</code> or{' '}
              <code>npm run hero:relevance:rollup</code> against the seller API container), then refresh.
            </Alert>
          )}

          {data.customer_products && data.customer_products.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top products for customer {customerIdSearch}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Score</TableCell>
                        <TableCell align="right">Link</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.customer_products.map((row) => (
                        <TableRow key={row.product_id}>
                          <TableCell>{row.product_name}</TableCell>
                          <TableCell align="right">{Number(row.score).toFixed(4)}</TableCell>
                          <TableCell align="right">
                            <Link
                              href={heroBestPriceUrl(Number(customerIdSearch), row.product_id)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Best price
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default DashboardProductAffinity;
